// Puzzle - Reconstitue l'image avec drag & drop et timer
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ArrowLeft, Loader2, Trophy, RefreshCw, Clock, Shuffle, Image } from 'lucide-react';
import { GAMES_CONFIG, calculatePoints, DIFFICULTY_MULTIPLIERS } from '../services/fanZoneConfig';
import { saveGameScore, hasPlayedToday, getDailyChallenge, createDailyChallenge } from '../services/fanZoneService';
import { db } from '../../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import logger from '../../../utils/logger';
import toast from 'react-hot-toast';

// Scrollbar styles
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar { width: 6px; }
  .fanzone-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

// Placeholder images si pas de galerie
const PLACEHOLDER_IMAGES = [
  '/gallery_placeholders/efafc_gallery_1.jpg',
  '/gallery_placeholders/efafc_gallery_2.jpg',
  '/gallery_placeholders/efafc_gallery_3.jpg',
  '/gallery_placeholders/efafc_gallery_4.jpg',
  '/gallery_placeholders/efafc_gallery_5.jpg',
];

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const PuzzlePiece = ({ piece, index, gridSize, pieceSize, onDragStart, onDragOver, onDrop, isDragging }) => {
  const correctRow = Math.floor(piece.originalIndex / gridSize);
  const correctCol = piece.originalIndex % gridSize;

  return (
    <motion.div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`
        cursor-grab active:cursor-grabbing rounded-lg overflow-hidden
        border-2 transition-colors duration-200
        ${isDragging ? 'border-[#FFE400] opacity-50' : 'border-white/10 hover:border-[#FFE400]/50'}
      `}
      style={{
        width: pieceSize,
        height: pieceSize,
        backgroundImage: `url(${piece.imageUrl})`,
        backgroundPosition: `-${correctCol * pieceSize}px -${correctRow * pieceSize}px`,
        backgroundSize: `${pieceSize * gridSize}px ${pieceSize * gridSize}px`,
      }}
    />
  );
};

const PuzzleGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  
  const difficulty = searchParams.get('difficulty') || 'facile';
  const config = GAMES_CONFIG.puzzle;
  const diffConfig = config.difficulties[difficulty];
  
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [pieces, setPieces] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(diffConfig?.timeLimit || 120);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  const gridSize = diffConfig?.grid || 3;
  const pieceSize = Math.min(300 / gridSize, 100);

  // Injecter styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Charger le jeu
  useEffect(() => {
    const loadGame = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        // Vérifier si déjà joué (ignorer erreurs permission)
        try {
          const played = await hasPlayedToday(currentUser.uid, 'puzzle', difficulty);
          if (played) {
            setAlreadyPlayed(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour hasPlayedToday');
        }

        // Récupérer le défi du jour (ignorer erreurs permission)
        let challenge = null;
        try {
          challenge = await getDailyChallenge(`puzzle_${difficulty}`);
        } catch (e) {
          logger.warn('Permission Firebase ignorée pour getDailyChallenge');
        }
        
        if (!challenge) {
          // Charger une image depuis la galerie
          let images = [];
          try {
            const galerieSnapshot = await getDocs(collection(db, 'galerie'));
            galerieSnapshot.forEach(doc => {
              const data = doc.data();
              if (data.url) {
                images.push(data.url);
              }
            });
          } catch (e) {
            logger.warn('Erreur chargement galerie');
          }

          // Fallback vers placeholder
          const imagePool = images.length > 0 ? images : PLACEHOLDER_IMAGES;
          const randomImage = imagePool[Math.floor(Math.random() * imagePool.length)];
          
          challenge = { imageUrl: randomImage };
          
          // Essayer de sauvegarder, ignorer si permission refusée
          try {
            await createDailyChallenge(`puzzle_${difficulty}`, challenge);
          } catch (e) {
            logger.warn('Impossible de sauvegarder le défi, mode démo actif');
          }
        }

        setImageUrl(challenge.imageUrl);
        initializePuzzle(challenge.imageUrl);
        
      } catch (error) {
        logger.error('Erreur chargement Puzzle:', error);
        // Fallback
        const fallbackImage = PLACEHOLDER_IMAGES[0];
        setImageUrl(fallbackImage);
        initializePuzzle(fallbackImage);
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [currentUser, difficulty, navigate]);

  const initializePuzzle = (imgUrl) => {
    const numPieces = gridSize * gridSize;
    const initialPieces = Array.from({ length: numPieces }, (_, i) => ({
      originalIndex: i,
      currentIndex: i,
      imageUrl: imgUrl
    }));
    
    // Mélanger les pièces
    const shuffled = [...initialPieces];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    shuffled.forEach((piece, idx) => {
      piece.currentIndex = idx;
    });
    
    setPieces(shuffled);
  };

  const startGame = () => {
    setGameStarted(true);
    setTimeRemaining(diffConfig.timeLimit);
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endGame = useCallback(async (isWin) => {
    clearInterval(timerRef.current);
    setGameOver(true);
    setWon(isWin);
    
    const points = isWin ? calculatePoints.puzzle(timeRemaining, diffConfig.timeLimit, difficulty) : 0;
    setPointsEarned(points);
    
    if (currentUser) {
      try {
        await saveGameScore(currentUser.uid, 'puzzle', difficulty, points, {
          won: isWin,
          timeRemaining,
          timeLimit: diffConfig.timeLimit
        });
      } catch (err) {
        logger.error('Erreur sauvegarde score:', err);
      }
    }
  }, [currentUser, difficulty, diffConfig?.timeLimit, timeRemaining]);

  const checkWin = useCallback((currentPieces) => {
    const isComplete = currentPieces.every((piece, idx) => piece.originalIndex === idx);
    if (isComplete && gameStarted && !gameOver) {
      endGame(true);
    }
  }, [gameStarted, gameOver, endGame]);

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    
    setPieces(prev => {
      const newPieces = [...prev];
      [newPieces[draggedIndex], newPieces[targetIndex]] = [newPieces[targetIndex], newPieces[draggedIndex]];
      newPieces[draggedIndex].currentIndex = draggedIndex;
      newPieces[targetIndex].currentIndex = targetIndex;
      
      // Vérifier victoire
      setTimeout(() => checkWin(newPieces), 100);
      
      return newPieces;
    });
    
    setDraggedIndex(null);
  };

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  if (alreadyPlayed) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <RefreshCw className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Déjà joué aujourd'hui !</h2>
          <p className="text-gray-400 mb-6">Reviens demain pour un nouveau défi</p>
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
          >
            Retour au dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
    >
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-sm">
            <span className="text-gray-400">{gridSize}x{gridSize}</span>
            <span className="text-[#FFE400] font-medium">
              x{DIFFICULTY_MULTIPLIERS[difficulty]}
            </span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="text-4xl mb-2">🧩</div>
          <h1 className="text-2xl font-bold text-white">Puzzle</h1>
          <p className="text-gray-400 text-sm mt-1">
            Reconstitue l'image en {formatTime(diffConfig.timeLimit)}
          </p>
        </motion.div>

        {/* Timer & Controls */}
        {gameStarted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-between mb-6 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <div className="flex items-center gap-2">
              <Clock className={`w-5 h-5 ${timeRemaining < 30 ? 'text-red-500' : 'text-[#FFE400]'}`} />
              <span className={`text-2xl font-bold ${timeRemaining < 30 ? 'text-red-500' : 'text-white'}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 text-gray-300 hover:text-white transition-colors"
            >
              <Image className="w-4 h-4" />
              <span className="text-sm">Aperçu</span>
            </button>
          </motion.div>
        )}

        {/* Start Screen */}
        {!gameStarted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div 
              className="relative mx-auto mb-6 rounded-xl overflow-hidden"
              style={{ width: pieceSize * gridSize, height: pieceSize * gridSize }}
            >
              <img 
                src={imageUrl} 
                alt="Puzzle" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = PLACEHOLDER_IMAGES[0];
                }}
              />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Shuffle className="w-12 h-12 text-white/50" />
              </div>
            </div>
            
            <button
              onClick={startGame}
              className="px-8 py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl text-lg"
            >
              Commencer le puzzle
            </button>
          </motion.div>
        )}

        {/* Puzzle Grid */}
        {gameStarted && !gameOver && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            ref={containerRef}
            className="mx-auto"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridSize}, ${pieceSize}px)`,
              gap: '4px',
              width: 'fit-content'
            }}
          >
            {pieces.map((piece, index) => (
              <PuzzlePiece
                key={piece.originalIndex}
                piece={piece}
                index={index}
                gridSize={gridSize}
                pieceSize={pieceSize}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragging={draggedIndex === index}
              />
            ))}
          </motion.div>
        )}

        {/* Preview Modal */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
              onClick={() => setShowPreview(false)}
            >
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                src={imageUrl}
                alt="Aperçu"
                className="max-w-sm w-full rounded-xl"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Over Modal */}
        <AnimatePresence>
          {gameOver && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="bg-[#0d1526] border border-white/10 rounded-2xl p-8 max-w-sm w-full text-center"
              >
                {won ? (
                  <>
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Puzzle terminé !</h2>
                    <p className="text-gray-400 mb-4">
                      Temps restant: <span className="text-[#FFE400] font-bold">{formatTime(timeRemaining)}</span>
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-4">⏰</div>
                    <h2 className="text-2xl font-bold text-white mb-2">Temps écoulé !</h2>
                    <p className="text-gray-400 mb-4">
                      Tu feras mieux la prochaine fois
                    </p>
                  </>
                )}
                
                {pointsEarned > 0 && (
                  <div className="flex items-center justify-center gap-2 mb-6 text-[#BDFF00]">
                    <Trophy className="w-5 h-5" />
                    <span className="text-xl font-bold">+{pointsEarned} XP</span>
                  </div>
                )}
                
                <button
                  onClick={() => navigate('/fanzone/dashboard')}
                  className="w-full py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
                >
                  Retour au dashboard
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PuzzleGame;
